import { useCallback } from 'react';
import type { ReactNode, RefObject } from 'react';

import type { HeaderGroup, Row, Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import { Box, ButtonBase, useTheme } from '@/shared/ui';
import { ArrowDownward, ArrowUpward, InfoOutlined, UnfoldMore } from '@/shared/ui/icons';

import { Z_INDEX } from '@/config/z-index';
import type { CatalogItem } from '@/entities/catalog-item';
import Tooltip from '@/shared/ui/primitives/Tooltip';
import { canExpandRow } from '../../../lib/canExpandRow';
import CatalogTableRow from '../CatalogTableRow/CatalogTableRow';
import SelectAllCheckbox from '../SelectAllCheckbox/SelectAllCheckbox';

interface CatalogTableGridProps {
  table: Table<CatalogItem>;
  rows: Row<CatalogItem>[];
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  isForwardCurves: boolean;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  forwardPeriodsInline: boolean;
  expandedData: boolean;
  periodGroups: Map<string, CatalogItem[]>;
  expandedCodes: Set<string>;
  fullyExpandedCodes: Set<string>;
  onToggleExpand: (code: string) => void;
  onShowAll: (code: string) => void;
}

const CatalogTableGrid = ({
  table,
  rows,
  rowVirtualizer,
  tableContainerRef,
  isForwardCurves,
  isInDashboard,
  renderFactsheetButton,
  forwardPeriodsInline,
  expandedData,
  periodGroups,
  expandedCodes,
  fullyExpandedCodes,
  onToggleExpand,
  onShowAll
}: CatalogTableGridProps) => {
  const { palette } = useTheme();
  // Stable across scroll; new reference only when a column is shown/hidden — the
  // signal each memoized row needs to re-derive its visible cells.
  const columnVisibility = table.getState().columnVisibility;

  // Memoized so its identity survives the frequent scroll-driven re-renders of
  // this grid (deps are unchanged during a scroll), rather than reallocating each
  // frame. Reports whether the row at `index` is an expanded period block.
  const isRowExpanded = useCallback(
    (index: number) => {
      const candidate = rows[index];
      if (!candidate) return false;
      const candidateCode = candidate.original.Code;
      return (
        canExpandRow({
          forwardPeriodsInline,
          isForwardCurves,
          expandedData,
          periodCount: periodGroups.get(candidateCode)?.length ?? 0
        }) && expandedCodes.has(candidateCode)
      );
    },
    [rows, periodGroups, expandedCodes, forwardPeriodsInline, isForwardCurves, expandedData]
  );

  return (
    <Box
      ref={tableContainerRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        position: 'relative',
        zIndex: Z_INDEX.sticky
      }}
    >
      <Box sx={{ display: 'grid' }}>
        <Box
          sx={{
            display: 'grid',
            position: 'sticky',
            top: 0,
            zIndex: Z_INDEX.raised,
            background: palette.gx.tileDark
          }}
        >
          {table.getHeaderGroups().map((headerGroup: HeaderGroup<CatalogItem>) => (
            <Box
              key={headerGroup.id}
              sx={{ display: 'flex', padding: '8px', paddingLeft: '16px' }}
            >
              {headerGroup.headers.map(header => (
                <Box
                  key={header.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: header.getSize(),
                    paddingBottom: '0',
                    paddingRight: '8px',
                    justifyContent: ['Last', 'Change'].includes(
                      header.column.columnDef.header as string
                    )
                      ? 'flex-end'
                      : 'flex-start'
                  }}
                >
                  {header.column.id === 'Symbol' ? (
                    <SelectAllCheckbox
                      isForwardCurves={isForwardCurves}
                      table={table}
                    />
                  ) : (
                    <ButtonBase
                      disableRipple
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        border: 'none',
                        color: 'inherit',
                        background: 'transparent',
                        fontFamily: 'inherit',
                        padding: 0,
                        margin: 0,
                        fontWeight: 700,
                        fontSize: '16px'
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.column.columnDef.header !== 'AdditionalItem' &&
                        flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.columnDef.header === 'Info' && (
                        <Tooltip title='Index information pages provide details of the methodology behind the index.'>
                          <InfoOutlined sx={{ ml: 0.5, fontSize: 12 }} />
                        </Tooltip>
                      )}
                      {(header.column.getCanSort() &&
                        header.column.columnDef.header !== 'Info' &&
                        header.column.columnDef.header !== 'AdditionalItem' &&
                        header.column.id !== 'Symbol' &&
                        {
                          asc: (
                            <ArrowUpward
                              fontSize='small'
                              sx={{ ml: 0.5 }}
                            />
                          ),
                          desc: (
                            <ArrowDownward
                              fontSize='small'
                              sx={{ ml: 0.5 }}
                            />
                          )
                        }[header.column.getIsSorted() as string]) ?? (
                        <UnfoldMore
                          fontSize='small'
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </ButtonBase>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        <Box sx={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map(virtualRow => {
            const row = rows[virtualRow.index];
            const code = row.original.Code;
            const group = periodGroups.get(code);
            const rowCanExpand = canExpandRow({
              forwardPeriodsInline,
              isForwardCurves,
              expandedData,
              periodCount: group?.length ?? 0
            });
            const isExpanded = rowCanExpand && expandedCodes.has(code);
            // Adjacent expanded blocks share one divider: this block drops its
            // top rule when the row above is also an expanded block.
            const collapseTopBorder = isExpanded && isRowExpanded(virtualRow.index - 1);
            return (
              <CatalogTableRow
                key={virtualRow.key}
                canExpand={rowCanExpand}
                collapseTopBorder={collapseTopBorder}
                columnVisibility={columnVisibility}
                isExpanded={isExpanded}
                isForwardCurves={isForwardCurves}
                isFullyExpanded={fullyExpandedCodes.has(code)}
                isInDashboard={isInDashboard}
                measureRef={rowVirtualizer.measureElement}
                onShowAll={onShowAll}
                onToggleExpand={onToggleExpand}
                periods={group}
                renderFactsheetButton={renderFactsheetButton}
                row={row}
                rowIndex={virtualRow.index}
                rowStart={virtualRow.start}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CatalogTableGrid;
